import Bookmark from "@mui/icons-material/Bookmark";
import EditRounded from "@mui/icons-material/EditRounded";
import { Grid, Theme, styled } from "@mui/material";
import { useTheme } from "@mui/styles";
import { IDisplayPairing, IPairingPosition } from "@vmosolutions/oc-interfaces";
import classNames from "classnames";
import React, { useCallback, useState } from "react";
import { useDrag } from "react-dnd";
import { useDispatch } from "react-redux";
import { handleEditTypes } from "./helpers";
import { ShortCodeContextMenu } from "./ShortCodeContextMenu";

export interface IShortCodeChip {
  itemId: string;
  code: string;
  type: "pairing" | "flight";
  // optional parameters
  /** true when this chip represents a planned pairing */
  isPlanned?: boolean;
  /** optional id of a parent context menu to close (e.g. CrewSchedule context menu) */
  contextMenuId?: string;
  /** if true, open editor for that given id and type on click. */
  edit?: boolean;
  className?: string;
  /** custom handler if not opening editor */
  onClickChip?: (pairingId: string) => void;
  style?: any;
  disableFocus?: boolean;
  disableContext?: boolean;
  startDate?: number;
  endDate?: number;
  positions?: IPairingPosition[];
}

const OCShortCodeChip = styled(Grid)(({ theme }) => ({
  position: "relative",
  userSelect: "none",
  width: 40, // length including interaction icon
  height: 20,
  justifyContent: "space-between",
  display: "flex",
  flexDirection: "row",
  margin: "2px 2px 2px 15px",
  borderRadius: 3,
  fontFamily: "B612 Mono, arial, sans-serif",
  fontSize: 12,
  padding: 5,
  alignItems: "center",
  backgroundColor: theme.palette.grey[300],
  color: theme.palette.background.paper,
  "&.edit": { width: 57 }, // length including interaction icon

  "&.draggable": {
    cursor: "grab",
    "&:active": { cursor: "grabbing" },
  },

  "& .type_icon": {
    transform: "rotateZ(90deg)scaleY(1.45)translateY(10px)",
    width: 34,
    height: 34,
    "& path": { fill: theme.palette.grey[300] },
  },

  "& .shortcode": {
    position: "absolute",
    fontSize: 12,
    color: theme.palette.background.paper,
    fontWeight: 700,
    marginLeft: -5,
  },

  "& .edit_btn": {
    width: 14,
    height: 14,
    cursor: "pointer",
    borderRadius: 3,
    border: `1px solid ${theme.palette.background.paper}`,
    "& path": { fill: theme.palette.background.paper },
    "&:hover": { width: 16, height: 16 },
    "&:active": { width: 14, height: 14 },
  },

  "&.interactive": {
    cursor: "pointer",
  },
}));

/**
 * ShortCodeChip - Component used to display/action shortcodes used throughout the app.
 * @param props - Component Properties [[IShortCodeChip]]
 */
export const ShortCodeChip = (props: IShortCodeChip): JSX.Element => {
  const {
    itemId,
    code,
    type,
    edit,
    className,
    onClickChip,
    style,
    disableContext,
    disableFocus,
    isPlanned,
    contextMenuId,
    startDate,
    endDate,
    positions,
  } = props;
  const theme: Theme = useTheme();
  const dispatch = useDispatch();
  const [contextAnchor, setContextAnchor] = useState(null);
  // Pairing has ID and isn't temp.
  const actualPairing: boolean = itemId && !itemId.includes("temp");
  const dragType = "PAIRING_EVENT";
  const pairingInfo: Partial<IDisplayPairing> = {
    _id: itemId,
    shortId: code,
    start: startDate,
    end: endDate,
    positions: positions ?? [],
  };
  const [collected, drag, dragPreview] = useDrag(
    {
      type: dragType,
      item: { pairingInfo: pairingInfo, type: dragType },
    },
    [dragType, pairingInfo]
  );

  // Set context menu anchor (opens it);
  const handlePopoverOpen = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      if (disableContext || !actualPairing) {
        return;
      }
      event.preventDefault();
      event.stopPropagation(); //! Ensure right click doesn't select if used in table row.
      setContextAnchor(event.currentTarget);
    },
    [disableContext, actualPairing]
  );

  // Fire off onClickChip if supplied by properties in valid pairing and not disabled
  const handleClickChip = useCallback(
    (event: React.MouseEvent): void => {
      if (actualPairing && onClickChip && typeof onClickChip === "function" && !disableFocus) {
        event.stopPropagation(); // important when used in the table where rows are clickable.
        onClickChip(itemId);
      }
    },
    [itemId, actualPairing, onClickChip, disableFocus]
  );

  return (
    <OCShortCodeChip
      item
      ref={drag && actualPairing && startDate && endDate ? drag : null} // only allow drag if actual pairing and has start/end info
      className={classNames(className, !!onClickChip && itemId && "interactive", edit && "edit", type)}
      onClick={handleClickChip}
      onContextMenu={handlePopoverOpen}
      style={style}
    >
      <Bookmark className="type_icon" />
      <Grid item className="shortcode">
        {code}
      </Grid>
      {!!edit && actualPairing && !isPlanned && (
        <EditRounded className="edit_btn" onClick={handleEditTypes(itemId, type, dispatch)} />
      )}

      {!disableContext && actualPairing && (
        <ShortCodeContextMenu
          pairingId={itemId}
          shortCode={code}
          isPlanned={isPlanned}
          contextMenuId={contextMenuId}
          anchorEl={contextAnchor}
          setAnchor={setContextAnchor}
        />
      )}
    </OCShortCodeChip>
  );
};
